import { useQuery } from "@tanstack/react-query";

import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { formatEvaluationWindowLabel } from "../../utils/fraudTimeUnits";
import { readComplianceBody } from "../helper/readComplianceBody";

type StoredToken = { AccessToken?: string };

/** Single step of the fraud rule (from `levels[]` on GET /v1/fraud/rule). */
export type FraudGlRuleLevel = {
  step: number;
  name: string;
  type: string;
  description: string;
  threshold: string | number | null;
};

/** Raw GET /v1/fraud/rule response. */
export type FraudGlRuleApi = {
  rule_id: string;
  rule_name: string;
  deposit_threshold: string | number;
  evaluation_window_seconds: number;
  active: boolean;
  updated_at: string;
  levels?: FraudGlRuleLevel[];
};

export type FraudRuleCondition = {
  threshold_value: string;
  period: string;
};

/** UI row for rules table (mapped from {@link FraudGlRuleApi}). */
export type FraudDetectionRuleData = {
  id: string;
  name: string;
  status: string;
  trigger_action: string[];
  conditions: FraudRuleCondition[];
  violators_count: number;
  last_modified: string | null;
  editor: string;
  deposit_threshold?: string | number;
  evaluation_window_seconds?: number;
  active?: boolean;
  /** Raw levels kept for downstream screens (e.g. EditRule). */
  levels?: FraudGlRuleLevel[];
};

const GLI_RULE_TRIGGER_LABEL = "Deposit playthrough (GLI)";

/**
 * Turns `levels[]` into the chips shown in the "Trigger action" column.
 * Falls back to a single generic label when the backend omits `levels`.
 */
function buildTriggerActionLabels(
  levels: FraudGlRuleLevel[] | undefined,
): string[] {
  if (!levels || levels.length === 0) return [GLI_RULE_TRIGGER_LABEL];
  return [...levels]
    .sort((a, b) => a.step - b.step)
    .map((lvl) => lvl.name?.trim() || lvl.type)
    .filter((label) => label);
}

/**
 * Builds one "Conditions" row per `level`, sharing the rule's single
 * `deposit_threshold` across all trigger actions (deposit, wager, withdrawal)
 * alongside the evaluation window. Falls back to a single row when `levels`
 * is absent.
 */
function buildConditionsFromLevels(
  rule: FraudGlRuleApi,
): FraudRuleCondition[] {
  const period = formatEvaluationWindowLabel(rule.evaluation_window_seconds);
  const thresholdValue = String(rule.deposit_threshold ?? "0");
  if (!rule.levels || rule.levels.length === 0) {
    return [{ threshold_value: thresholdValue, period }];
  }
  return [...rule.levels]
    .sort((a, b) => a.step - b.step)
    .map(() => ({
      threshold_value: thresholdValue,
      period,
    }));
}

export function mapGlRuleApiToRuleData(rule: FraudGlRuleApi): FraudDetectionRuleData {
  return {
    id: rule.rule_id,
    name: rule.rule_name,
    status: rule.active ? "Active" : "Inactive",
    trigger_action: buildTriggerActionLabels(rule.levels),
    conditions: buildConditionsFromLevels(rule),
    violators_count: 0,
    last_modified: rule.updated_at,
    editor: "—",
    deposit_threshold: rule.deposit_threshold,
    evaluation_window_seconds: rule.evaluation_window_seconds,
    active: rule.active,
    levels: rule.levels,
  };
}

const fetchFraudRules = async (): Promise<FraudDetectionRuleData[]> => {
  const tokenString = localStorage.getItem("token");
  const token: StoredToken | null = tokenString ? JSON.parse(tokenString) : null;
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(token?.AccessToken);

  const response = await PrivateAdminPanelInstance.get(API_ROUTES.FRAUD_RULE);
  const body = readComplianceBody<FraudGlRuleApi>(response);
  if (!body?.rule_id) return [];
  return [mapGlRuleApiToRuleData(body)];
};

export const useGetFraudRules = () => {
  return useQuery({
    queryKey: ["GET_FRAUD_RULE"],
    queryFn: fetchFraudRules,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
